/* DocKit - shared building blocks for the Eleven Family Office document system:
   content renderer, block editor with placeholder/text-block pickers,
   status badges, PDF/file download helpers and the generator dialog. */
import { useEffect, useState } from "react";
import {
  ArrowDown, ArrowUp, Braces, FileText, Heading2, List as ListIcon,
  Loader2, Plus, PlusSquare, Sparkles, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

export const STATUS_META = {
  draft: { label: "Entwurf", en: "Draft", cls: "border-[#D7DADF] text-[#3B4452]" },
  in_review: { label: "In Pr\u00fcfung", en: "In review", cls: "border-amber-400/60 text-amber-700" },
  approved: { label: "Freigegeben", en: "Approved", cls: "border-sky-400/60 text-sky-700" },
  published: { label: "Ver\u00f6ffentlicht", en: "Published", cls: "border-emerald-500/60 text-emerald-700" },
  archived: { label: "Archiviert", en: "Archived", cls: "border-[#D7DADF] text-[#9AA1AB]" },
  superseded: { label: "Ersetzt", en: "Superseded", cls: "border-[#D7DADF] text-[#9AA1AB]" },
};

export function StatusBadge({ status }) {
  const { t } = useLang();
  const meta = STATUS_META[status] || STATUS_META.draft;
  return (
    <Badge variant="outline" className={`rounded-full text-[10px] ${meta.cls}`} data-testid={`status-badge-${status}`}>
      {t(meta.label, meta.en)}
    </Badge>
  );
}

/* ---------- inline **bold** rendering ---------- */
export function mdInline(text, keyPrefix = "k") {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={`${keyPrefix}-${i}`} className="font-semibold text-[#111318]">{part.slice(2, -2)}</strong>
    ) : (
      <span key={`${keyPrefix}-${i}`}>{part}</span>
    )
  );
}

export function DocContentView({ content }) {
  return (
    <div className="space-y-2.5" data-testid="doc-content-view">
      {(content || []).map((block, i) => {
        if (block.type === "heading") {
          return <h3 key={i} className="font-display text-lg font-semibold text-[#111318] pt-2">{mdInline(block.text, `h${i}`)}</h3>;
        }
        if (block.type === "list") {
          return (
            <ul key={i} className="space-y-1.5 pl-1">
              {(block.items || []).map((item, j) => (
                <li key={j} className="flex gap-2 text-sm leading-relaxed">
                  <span className="mt-[8px] h-1 w-1 rounded-full bg-[#FF3C20] shrink-0" />
                  <span>{mdInline(item, `l${i}-${j}`)}</span>
                </li>
              ))}
            </ul>
          );
        }
        return <p key={i} className="text-sm leading-relaxed text-foreground/85 min-h-[8px]">{mdInline(block.text, `p${i}`)}</p>;
      })}
    </div>
  );
}

/* ---------- blob download helper ---------- */
export async function downloadApiFile(path, fallbackName = "dokument.pdf") {
  const res = await api.get(path, { responseType: "blob" });
  const disposition = res.headers["content-disposition"] || "";
  let filename = fallbackName;
  const star = disposition.match(/filename\*=UTF-8''([^;]+)/);
  const plain = disposition.match(/filename="([^"]+)"/);
  if (star) filename = decodeURIComponent(star[1]);
  else if (plain) filename = plain[1];
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ---------- content blocks editor ---------- */
export function ContentBlocksEditor({ content, onChange, placeholders = [], blocks = [] }) {
  const { t } = useLang();
  const [activeIdx, setActiveIdx] = useState(null);
  const list = content || [];

  const update = (i, patch) => {
    const next = [...list];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, x) => x !== i));
  const add = (type) => onChange([...list, type === "list" ? { type, items: [""] } : { type, text: "" }]);

  const insertPlaceholder = (key) => {
    if (activeIdx === null || !list[activeIdx]) {
      toast.info(t("Bitte zuerst einen Block anklicken", "Please click into a block first"));
      return;
    }
    const b = list[activeIdx];
    if (b.type === "list") {
      const items = [...(b.items || [])];
      items[items.length - 1] = `${items[items.length - 1] || ""} {{${key}}}`.trim();
      update(activeIdx, { items });
    } else {
      update(activeIdx, { text: `${b.text || ""} {{${key}}}`.trim() });
    }
  };

  const insertBlockText = (blk) => {
    onChange([...list, { type: "paragraph", text: blk.content }]);
    toast.success(t("Textbaustein eingef\u00fcgt", "Text block inserted"));
  };

  return (
    <div className="space-y-3" data-testid="content-blocks-editor">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" className="rounded-[8px]" onClick={() => add("heading")} data-testid="add-heading-block">
          <Heading2 className="h-3.5 w-3.5 mr-1" /> {t("\u00dcberschrift", "Heading")}
        </Button>
        <Button type="button" size="sm" variant="outline" className="rounded-[8px]" onClick={() => add("paragraph")} data-testid="add-paragraph-block">
          <FileText className="h-3.5 w-3.5 mr-1" /> {t("Absatz", "Paragraph")}
        </Button>
        <Button type="button" size="sm" variant="outline" className="rounded-[8px]" onClick={() => add("list")} data-testid="add-list-block">
          <ListIcon className="h-3.5 w-3.5 mr-1" /> {t("Liste", "List")}
        </Button>
        {placeholders.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" size="sm" variant="outline" className="rounded-[8px] border-[#D6C08D]/50 text-[#A8905A]" data-testid="placeholder-picker-button">
                <Braces className="h-3.5 w-3.5 mr-1" /> {t("Platzhalter", "Placeholder")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="start">
              <ScrollArea className="h-72">
                <div className="p-3 space-y-3">
                  {placeholders.map((group) => (
                    <div key={group.group}>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {group.keys.map((k) => (
                          <button
                            key={k.key}
                            onClick={() => insertPlaceholder(k.key)}
                            className="rounded-full border border-border px-2 py-0.5 text-[11px] hover:bg-[#D6C08D]/10 hover:border-[#D6C08D]/50 transition-colors"
                            title={`{{${k.key}}}`}
                            data-testid={`placeholder-${k.key}`}
                          >
                            {k.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
        )}
        {blocks.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" size="sm" variant="outline" className="rounded-[8px] border-[#D6C08D]/50 text-[#A8905A]" data-testid="textblock-picker-button">
                <PlusSquare className="h-3.5 w-3.5 mr-1" /> {t("Textbaustein", "Text block")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <ScrollArea className="h-72">
                <div className="p-2">
                  {blocks.map((blk) => (
                    <button
                      key={blk.id}
                      onClick={() => insertBlockText(blk)}
                      className="w-full text-left rounded-[8px] px-3 py-2 hover:bg-muted transition-colors"
                      data-testid={`textblock-${blk.id}`}
                    >
                      <span className="text-xs font-medium flex items-center gap-1.5">
                        {blk.title}
                        {blk.required && <Badge variant="outline" className="rounded-full text-[9px] border-[#D6C08D]/50 text-[#A8905A]">Pflicht</Badge>}
                      </span>
                      <span className="block text-[11px] text-muted-foreground truncate">{blk.content}</span>
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {list.length === 0 && (
        <p className="text-sm text-muted-foreground border border-dashed border-border rounded-[8px] p-6 text-center">
          {t("Noch keine Inhalte. F\u00fcgen Sie \u00dcberschriften, Abs\u00e4tze oder Listen hinzu.", "No content yet. Add headings, paragraphs or lists.")}
        </p>
      )}

      {list.map((block, i) => (
        <div key={i} className={`rounded-[8px] border p-3 bg-white ${activeIdx === i ? "border-[#D6C08D]" : "border-border"}`} onClick={() => setActiveIdx(i)} data-testid={`content-block-${i}`}>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="secondary" className="rounded-full text-[9px] uppercase">
              {block.type === "heading" ? t("\u00dcberschrift", "Heading") : block.type === "list" ? t("Liste", "List") : t("Absatz", "Paragraph")}
            </Badge>
            <div className="ml-auto flex">
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-[8px]" onClick={(e) => { e.stopPropagation(); move(i, -1); }}><ArrowUp className="h-3.5 w-3.5" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-[8px]" onClick={(e) => { e.stopPropagation(); move(i, 1); }}><ArrowDown className="h-3.5 w-3.5" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-[8px] text-destructive" onClick={(e) => { e.stopPropagation(); remove(i); }} data-testid={`content-block-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          {block.type === "list" ? (
            <div className="space-y-1.5">
              {(block.items || []).map((item, j) => (
                <div key={j} className="flex items-center gap-2">
                  <span className="h-1 w-1 rounded-full bg-[#FF3C20] shrink-0" />
                  <Input
                    value={item}
                    onFocus={() => setActiveIdx(i)}
                    onChange={(e) => {
                      const items = [...block.items];
                      items[j] = e.target.value;
                      update(i, { items });
                    }}
                    className="rounded-[8px] h-8 text-sm"
                  />
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7 rounded-[8px] text-destructive shrink-0" onClick={() => update(i, { items: block.items.filter((_, x) => x !== j) })}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="ghost" size="sm" className="rounded-[8px] text-xs" onClick={() => update(i, { items: [...(block.items || []), ""] })}>
                <Plus className="h-3 w-3 mr-1" /> {t("Punkt", "Item")}
              </Button>
            </div>
          ) : (
            <Textarea
              value={block.text || ""}
              onFocus={() => setActiveIdx(i)}
              onChange={(e) => update(i, { text: e.target.value })}
              rows={block.type === "heading" ? 1 : 3}
              className="rounded-[8px] text-sm"
              placeholder={block.type === "heading" ? t("\u00dcberschrift\u2026", "Heading\u2026") : t("Text\u2026 (**fett** m\u00f6glich, {{platzhalter}} m\u00f6glich)", "Text\u2026 (**bold** and {{placeholders}} supported)")}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------- generator dialog ---------- */
export function GenerateDocumentDialog({ open, onOpenChange, contextType = null, contextId = null, presetLeadId = null, onCreated }) {
  const { t } = useLang();
  const [templates, setTemplates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [sourceType, setSourceType] = useState(presetLeadId ? "lead" : "manual");
  const [sourceId, setSourceId] = useState(presetLeadId || "");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/docs/templates?status=published").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/leads?limit=100").then((r) => setLeads(Array.isArray(r.data) ? r.data : r.data.items || [])).catch(() => setLeads([]));
    api.get("/admin/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
    setPreview(null);
    setTemplateId("");
    setSourceType(presetLeadId ? "lead" : "manual");
    setSourceId(presetLeadId || "");
  }, [open, presetLeadId]);

  const dataSource = () => ({ type: sourceType, id: sourceId || undefined });

  const doPreview = async () => {
    if (!templateId) return toast.error(t("Bitte Vorlage w\u00e4hlen", "Please select a template"));
    try {
      setBusy(true);
      const res = await api.post(`/docs/templates/${templateId}/preview`, { data_source: dataSource() });
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Vorschau fehlgeschlagen", "Preview failed"));
    } finally {
      setBusy(false);
    }
  };

  const doGenerate = async () => {
    if (!templateId) return toast.error(t("Bitte Vorlage w\u00e4hlen", "Please select a template"));
    try {
      setBusy(true);
      const res = await api.post("/docs/generate", {
        template_id: templateId,
        data_source: dataSource(),
        context_type: contextType || (sourceType === "lead" && sourceId ? "lead" : null),
        context_id: contextId || (sourceType === "lead" ? sourceId : null),
      });
      toast.success(t("Dokument erstellt", "Document created"));
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Fehler beim Erstellen", "Error creating"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="generate-document-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#A8905A]" /> {t("Dokument aus Vorlage erstellen", "Create document from template")}
          </DialogTitle>
          <DialogDescription>
            {t("Vorlage w\u00e4hlen, Datenquelle festlegen \u2013 Platzhalter werden automatisch bef\u00fcllt.", "Choose a template and data source \u2013 placeholders are filled automatically.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t("Vorlage", "Template")}</Label>
            <Select value={templateId} onValueChange={(v) => { setTemplateId(v); setPreview(null); }}>
              <SelectTrigger className="rounded-[6px]" data-testid="generator-template-select"><SelectValue placeholder={t("Vorlage w\u00e4hlen\u2026", "Select template\u2026")} /></SelectTrigger>
              <SelectContent>
                {templates.map((tp) => <SelectItem key={tp.id} value={tp.id}>{tp.name} (v{tp.version})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("Datenquelle", "Data source")}</Label>
              <Select value={sourceType} onValueChange={(v) => { setSourceType(v); setSourceId(v === "lead" ? presetLeadId || "" : ""); setPreview(null); }}>
                <SelectTrigger className="rounded-[6px]" data-testid="generator-source-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead">Lead</SelectItem>
                  {users.length > 0 && <SelectItem value="user">{t("Mitarbeiter", "Employee")}</SelectItem>}
                  <SelectItem value="manual">{t("Manuell / leer", "Manual / empty")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {sourceType === "lead" && (
              <div className="space-y-1.5">
                <Label>Lead</Label>
                <Select value={sourceId} onValueChange={(v) => { setSourceId(v); setPreview(null); }}>
                  <SelectTrigger className="rounded-[6px]" data-testid="generator-lead-select"><SelectValue placeholder={t("Lead w\u00e4hlen\u2026", "Select lead\u2026")} /></SelectTrigger>
                  <SelectContent>
                    {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.first_name} {l.last_name} {"\u00b7"} {l.product_interest}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {sourceType === "user" && (
              <div className="space-y-1.5">
                <Label>{t("Mitarbeiter", "Employee")}</Label>
                <Select value={sourceId} onValueChange={(v) => { setSourceId(v); setPreview(null); }}>
                  <SelectTrigger className="rounded-[6px]" data-testid="generator-user-select"><SelectValue placeholder={t("Mitarbeiter w\u00e4hlen\u2026", "Select employee\u2026")} /></SelectTrigger>
                  <SelectContent>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name} {"\u00b7"} {u.role}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {preview && (
            <div className="rounded-[8px] border border-border bg-muted/30 p-4 max-h-[300px] overflow-y-auto" data-testid="generator-preview">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{t("Vorschau", "Preview")}: {preview.name}</p>
              <DocContentView content={preview.content} />
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-[6px]" onClick={doPreview} disabled={busy} data-testid="generator-preview-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Vorschau", "Preview")}
          </Button>
          <Button className="rounded-[6px] bg-[#111318]" onClick={doGenerate} disabled={busy} data-testid="generator-create-button">
            {t("Dokument erstellen", "Create document")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
